/**
 * RefineTabContent — Refine tab for the UnifiedBottomPanel.
 *
 * Canvas UI 2.0 layout, top to bottom:
 *   References row (optional) | Feedback box | Prompt library | Control bar
 *
 * The references row and the prompt library are both OPT-IN: a caller that
 * passes no `references`/`onReferencesChange` gets the previous text-only tab
 * unchanged, and the library renders nothing for a node type with no curated
 * set. That keeps ImageGenerator and StoryboardDesigner exactly as they were
 * while the three designers pick up the new frames.
 *
 * Style and model ARE shown here. An earlier revision removed the style
 * selector on the reasoning that a refine only edits the previous image, but
 * the Canvas UI 2.0 frames put both back in the panel's bottom row — the same
 * row the Config tab uses — so a refine can change model or style without
 * leaving the tab.
 */

import { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactFlow } from 'reactflow'
import { AlertTriangle, ChevronDown, Image as ImageIcon } from 'lucide-react'
import {
  CanvasRichTextBox,
  type RichTextBoxHandle,
  type RichTextBoxState,
} from './RichTextBox'
import { CREATIVE_TEXT_MAX_LENGTH, logCreativeTextLimitConfigured } from './descriptionLimits'
import { ReferenceRow } from './ReferenceRow'
import { StyleSlot } from './StyleSlot'
import { ReferenceSelector, type SelectedReference } from './ReferenceSelector'
import { PromptLibrarySelect } from './PromptLibrarySelect'
import { StyleLibraryModal } from './StyleLibraryModal'
import { FlyoutPortal } from './FlyoutPortal'
import {
  IMAGE_MODEL_OPTIONS,
  ModelOnlyFlyout,
} from './ConfigTabContent'
import type { ImageModelOption } from './imageModelOptions'
import { getImageModelAlias, getImageModelLabel } from '../../../../constants/modelAliases'
import {
  AnnotationEditorOverlay,
  type AnnotationEditorHandle,
} from './annotate'
import { api, ApiRequestError } from '../../../../api/client'
import { getAssetUrl } from '../../../../api/useAssetUrl'
import { toast } from 'sonner'
import { getImageReferenceLimit, getImageReservedStyleSlots } from './imageConstraints'
import { useReadOnly } from '../../../../contexts/ReadOnlyContext'
import { useImageModels } from '../../../../hooks/useImageModels'
import { useOptionalProjectId } from '../../../../contexts/ProjectIdContext'
import { createNadDebug } from '../../../../utils/debugLog'
import { trackInteraction } from '../../../../analytics/interactionAnalytics'
import { analyticsNodeType } from '../../../../analytics/nodeAnalytics'
import type { RefineControlAction } from '../../../../analytics/generated/analyticsEvents'
import { imageSparksPerGeneration } from '../../../../generated/nodeDataTypes'
import { CreditQuantityPill } from './CreditQuantityPill'
import type { ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const FEEDBACK_MAX_LENGTH = CREATIVE_TEXT_MAX_LENGTH

const refineTextFieldLog = createNadDebug('dw:refine-text-field:layout')
const refineVariantCountLog = createNadDebug('dw:refine-variant-count')
// Dev-gated (VITE_NAD_DEBUG) trace of the (model, quality, count) -> sparks
// resolution so the demo timeline shows the number feeding the refine pill.
const refineQuoteLog = createNadDebug('dw:image-pricing:refine-quote')
const annotationBakeLog = createNadDebug('dw:refine-annotation')
const refineControlsLog = createNadDebug('dw:refine-controls', { priority: true })

/** Node-type-specific placeholder text for the feedback textarea */
export const REFINE_PLACEHOLDERS: Record<string, string> = {
  characterDesigner: 'e.g. make hair darker, add details to outfit...',
  locationDesigner: 'e.g. warmer atmosphere, add ambient lighting...',
  objectDesigner: 'e.g. add more detail, change the material...',
  imageGenerator: 'e.g. make it brighter, add more contrast...',
  storyboardDesigner: 'e.g. change the camera angle, more dramatic lighting...',
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StyleOption {
  id: string
  name: string
}

export interface RefineTabContentProps {
  /** Stable ID of the canvas node that owns this refine editor. */
  nodeId: string
  /** Current feedback text */
  feedbackText: string
  /** Feedback text change callback */
  onFeedbackTextChange: (text: string) => void
  /** Submit callback — receives retry count */
  /**
   * Submit the refine. `annotatedImagePath` carries a freshly baked annotation
   * for this run only; the owner forwards it as a `state_updates` argument.
   */
  onSubmit: (retryCount?: number, options?: { annotatedImagePath?: string }) => void
  /** Whether the node is currently running */
  isRunning: boolean
  /** Node type for placeholder text */
  nodeType?: string
  /** Optional custom placeholder override */
  placeholder?: string
  /** Available styles for the inline dropdown */
  styles?: StyleOption[]
  /** Currently selected style ID */
  selectedStyleId?: string
  /** Style selection callback */
  onStyleChange?: (styleId: string) => void
  /** Model selection callback. Omit to render the model pill read-only. */
  onModelChange?: (model: string) => void
  /**
   * Whether to render the retry/variant-count selector pill (default true).
   * Pass false for nodes whose backend ignores the orchestrator count (e.g.
   * the storyboard designer, which fans out by grid decomposition and does
   * `del count`). The sparks-count span still renders; `retryCount` stays
   * pinned to 1 so `onSubmit(1)` is sent.
   *
   * The sparks quote also pins to ×1 when this is false, so the quote matches
   * the actual charge for nodes whose count is dropped backend-side.
   */
  showRetrySelector?: boolean
  /**
   * Backend image-pricing model key (in_model) used to quote refine sparks via
   * the generated mirror, so the pill matches what executeNode bills. When
   * omitted/unknown the pill shows '—'. Plumbed from each parent node.
   */
  model?: string
  /**
   * Image quality (in_quality) for tiered gemini-3.1 pricing. Only
   * image_generator exposes quality; the designers/storyboard have no quality
   * port so they pass undefined (gemini-3.1 → 1k_2k tier).
   */
  quality?: string
  /** Image aspect ratio, used only for GPT Image 2's size-dependent estimate. */
  aspectRatio?: string
  /**
   * References attached to the node. Omit to hide the row entirely — that is
   * what keeps ImageGenerator's and StoryboardDesigner's Refine tab unchanged.
   * Supplying it without `onReferencesChange` renders the row read-only-ish
   * (the picker cannot save), so pass both or neither.
   */
  references?: SelectedReference[]
  /** Persist a reference change. Required for the row to be interactive. */
  onReferencesChange?: (refs: SelectedReference[]) => void
  /** Backend node type for the reference picker's media-tab resolution. */
  targetBackendType?: string
  /**
   * Project-relative path of the image the refine will edit — the currently
   * picked variant. Supplying it turns on the inline annotation surface; the
   * user draws on this image and Generate bakes the marks over it.
   *
   * Omit for nodes with no picked image yet, or with no frame specifying the
   * surface: the tab then renders exactly as before.
   */
  annotationSourcePath?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const RefineTabContent = memo(function RefineTabContent({
  nodeId,
  feedbackText,
  onFeedbackTextChange,
  onSubmit,
  isRunning,
  nodeType,
  placeholder,
  selectedStyleId,
  onStyleChange,
  onModelChange,
  showRetrySelector = true,
  model,
  quality,
  aspectRatio,
  references,
  onReferencesChange,
  targetBackendType,
  annotationSourcePath,
}: RefineTabContentProps) {
  const { t } = useTranslation('canvas')
  const readOnly = useReadOnly()
  // Optional, not `useProjectId`: this tab is rendered bare in unit tests and
  // on surfaces without a provider, and the previous text-only Refine had no
  // project dependency at all. No project simply means no reference row.
  const projectId = useOptionalProjectId()
  const { getNodes } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const [retryCount, setRetryCount] = useState(1)
  const [isRetryPopoverOpen, setIsRetryPopoverOpen] = useState(false)
  const [isRefSelectorOpen, setIsRefSelectorOpen] = useState(false)
  const [isBaking, setIsBaking] = useState(false)
  const [isStyleModalOpen, setIsStyleModalOpen] = useState(false)
  const [isModelFlyoutOpen, setIsModelFlyoutOpen] = useState(false)
  const modelPillRef = useRef<HTMLButtonElement>(null)
  const annotationRef = useRef<AnnotationEditorHandle | null>(null)
  const feedbackRef = useRef<RichTextBoxHandle | null>(null)
  const showAnnotationSurface = Boolean(annotationSourcePath && projectId && !readOnly)

  const trackControl = useCallback((action: RefineControlAction) => {
    if (projectId) {
      trackInteraction('canvas_refine_control_action', {
        project_id: projectId,
        node_id: nodeId,
        node_type: analyticsNodeType(nodeType ?? 'unknown'),
        action,
        style_id: selectedStyleId,
      })
    }
  }, [projectId, nodeId, nodeType, selectedStyleId])

  const logControl = useCallback((event: string, extra: { model_before?: string; model_after?: string; style_before?: string; style_after?: string } = {}) => {
    refineControlsLog({
      event,
      feature: 'refine-controls',
      trace_id: `refine-controls:${nodeId}`,
      node_id: nodeId,
      node_type: nodeType ?? 'unknown',
      ...extra,
    })
  }, [nodeId, nodeType])

  // The row is opt-in, and read-only mode has no business opening a picker.
  const showReferenceRow = references !== undefined && !readOnly && !!projectId
  // Deliberately NOT defaulting `model` the way ConfigTabContent does: the
  // sparks quote below keys off `model` being undefined to show '—', so a
  // default here would silently start quoting a model the caller never set.
  // Falls back to the same 4 Config does for BYOM / undeclared ids.
  const imageReferenceLimit = (model ? getImageReferenceLimit(model) : null) ?? 4
  const imageReservedStyleSlots = model ? getImageReservedStyleSlots(model) : 0

  const handleRefSave = useCallback(
    (selected: SelectedReference[]) => {
      onReferencesChange?.(selected)
      setIsRefSelectorOpen(false)
    },
    [onReferencesChange],
  )

  const retryPillRef = useRef<HTMLButtonElement>(null)
  const retryPopoverRef = useRef<HTMLDivElement>(null)
  const feedbackAreaRef = useRef<HTMLDivElement>(null)
  const {
    native: apiImageModels,
    byom: byomImageModels,
    isLoading: imageModelsLoading,
    error: imageModelsError,
  } = useImageModels()
  // The SAME list the Config tab offers, built the same way — BYOM entries the
  // user has enabled, then the natively advertised models. Refine previously
  // had no picker at all, so it could not drift from Config; now that it does,
  // it must share the construction rather than assemble a second, subtly
  // different set.
  const imageModelOptions = useMemo<ImageModelOption[]>(() => {
    const values = apiImageModels.length
      ? apiImageModels
      : IMAGE_MODEL_OPTIONS.map((option) => option.value)
    return [
      ...byomImageModels
        .filter((item) => item.enabled)
        .map((item) => ({
          value: item.id,
          label: item.key_name || `${t('projectConfigGear.myImageModel')} (${item.id.slice(-8)})`,
        })),
      ...values.map((value) => ({ value, label: getImageModelLabel(value) })),
    ]
  }, [apiImageModels, byomImageModels, t])
  const imageModelOptionValues = useMemo(
    () => new Set(imageModelOptions.map((option) => option.value)),
    [imageModelOptions],
  )
  const imageModelAvailabilityKnown = !imageModelsLoading && imageModelsError == null
  const isImageModelUnavailable = Boolean(
    model && imageModelAvailabilityKnown && !imageModelOptionValues.has(model),
  )
  const unavailableImageModelMessage = t('refineTabContent.unavailableModel')
  // Same precedence Config uses, so the two tabs never name one model
  // differently: flag it unavailable, else the display alias, else the option
  // label (which is where a BYOM key name comes from), else a generic word.
  const imageModelLabel = isImageModelUnavailable
    ? t('configTabContent.unavailableModel')
    : getImageModelAlias(model ?? '')
      ?? imageModelOptions.find((option) => option.value === model)?.label
      ?? t('configTabContent.modelFallback')

  // Refine quote = per-image sparks (generated from google_pricing.json) ×
  // effective count. effectiveRefineCount must equal what executeNode actually
  // bills: character/location/object/image_generator forward retryCount; the
  // storyboard refine drops the count backend-side (showRetrySelector=false), so
  // it is always billed as ×1 — pin the quote to 1 there too. perImageSparks is
  // undefined for an unknown model → pill shows '—' (matches the Config tab).
  const perImageSparks = model ? imageSparksPerGeneration(model, quality, aspectRatio) : 0
  const effectiveRefineCount = showRetrySelector ? retryCount : 1
  const sparksCount = perImageSparks > 0 ? perImageSparks * effectiveRefineCount : undefined
  useEffect(() => {
    refineQuoteLog({
      event: 'refine-quote',
      nodeType: nodeType ?? 'unknown',
      model: model ?? null,
      quality: quality ?? null,
      aspectRatio: aspectRatio ?? null,
      perImageSparks,
      retryCount,
      effectiveRefineCount,
      showRetrySelector,
      sparks: sparksCount ?? null,
    })
  }, [
    nodeType,
    model,
    quality,
    aspectRatio,
    perImageSparks,
    retryCount,
    effectiveRefineCount,
    showRetrySelector,
    sparksCount,
  ])

  useEffect(() => {
    logCreativeTextLimitConfigured('refine-tab-feedback', { nodeType })
  }, [nodeType])

  const resolvedPlaceholder =
    placeholder ??
    (nodeType && REFINE_PLACEHOLDERS[nodeType] ? t(`refineTabContent.${nodeType}`) : undefined) ??
    t('refineTabContent.defaultPlaceholder')

  // ── Click-outside for retry popover ───────────────────────────────────────
  useEffect(() => {
    if (!isRetryPopoverOpen) return

    const handlePointerDown = (e: PointerEvent) => {
      if (!(e.target instanceof HTMLElement)) return
      const target = e.target
      if (
        retryPopoverRef.current &&
        !retryPopoverRef.current.contains(target) &&
        retryPillRef.current &&
        !retryPillRef.current.contains(target)
      ) {
        setIsRetryPopoverOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [isRetryPopoverOpen])

  /**
   * Drop a library prompt in at the caret.
   *
   * Was `onFeedbackTextChange(prompt)`, which REPLACED the whole box — so a
   * user who had already typed lost it, and a second pick overwrote the first.
   * `insertAtCursor` also handles the caret being outside the editor (it falls
   * back to the end), which is the common case here: the click that opens the
   * dropdown moves focus off the textarea.
   */
  const handleLibraryInsert = useCallback(
    (prompt: string) => {
      const editor = feedbackRef.current
      if (!editor) {
        onFeedbackTextChange(prompt)
        return
      }
      // Separate from existing prose so an insert cannot produce a run-on like
      // "make it darkerModify facial expression". Leading whitespace is
      // harmless — `useFeedbackMode` trims before it writes `in_feedback`.
      const existing = editor.getPlainText()
      editor.insertAtCursor(existing.trim() ? ` ${prompt}` : prompt)
    },
    [onFeedbackTextChange],
  )

  const handleRichTextChange = useCallback(
    (state: RichTextBoxState) => {
      if (state.plainText.length <= FEEDBACK_MAX_LENGTH) {
        onFeedbackTextChange(state.plainText)
      }
    },
    [onFeedbackTextChange]
  )

  const handleGenerate = useCallback(() => {
    if (isImageModelUnavailable || isBaking) return

    // Nothing drawn (or no surface at all) → the refine we always had.
    const annotations = annotationRef.current?.collectAnnotations() ?? null
    if (!annotations || !projectId || !annotationSourcePath) {
      refineVariantCountLog({
        event: 'refine-submit',
        nodeType: nodeType ?? 'unknown',
        retryCount,
        annotated: false,
      })
      onSubmit(retryCount)
      return
    }

    // Bake FIRST, submit second. Firing the run before the bake resolves would
    // regenerate from the clean image and silently discard the drawing — the
    // exact failure this feature exists to avoid — so a bake error aborts the
    // whole submit rather than degrading to a plain refine.
    setIsBaking(true)
    void (async () => {
      try {
        const baked = await api.annotateAsset(projectId, annotationSourcePath, annotations)
        annotationBakeLog({
          event: 'refine-annotation:baked',
          trace_id: `refine-annotation:${nodeId}`,
          node_id: nodeId,
          node_type: nodeType ?? 'unknown',
          element_count: annotations.length,
          source_path: annotationSourcePath,
          baked_path: baked.path,
        })
        refineVariantCountLog({
          event: 'refine-submit',
          nodeType: nodeType ?? 'unknown',
          retryCount,
          annotated: true,
        })
        onSubmit(retryCount, { annotatedImagePath: baked.path })
      } catch (error) {
        // Surface the backend's own reason (Dev Principle 7): a rejected
        // coordinate or an unresolvable font is the caller's to fix, and a
        // generic failure hides which of the marks was bad.
        const status = error instanceof ApiRequestError ? error.status : 0
        const message = error instanceof Error ? error.message : String(error)
        annotationBakeLog({
          event: 'refine-annotation:bake_failed',
          trace_id: `refine-annotation:${nodeId}`,
          node_id: nodeId,
          status,
          reason: message,
        })
        toast.error(t('annotate.saveFailed', { message }))
      } finally {
        setIsBaking(false)
      }
    })()
  }, [
    annotationSourcePath,
    isBaking,
    isImageModelUnavailable,
    nodeId,
    nodeType,
    onSubmit,
    projectId,
    retryCount,
    t,
  ])

  useEffect(() => {
    const measureLayout = () => {
      const feedbackArea = feedbackAreaRef.current
      const container = feedbackArea?.querySelector<HTMLElement>('[data-testid="refine-feedback-textarea"]')
      const editor = feedbackArea?.querySelector<HTMLElement>('[data-testid="rich-text-editor"]')
      if (!feedbackArea || !container || !editor) return

      refineTextFieldLog({
        event: 'refine-feedback-layout',
        trace_id: 'refine-text-field:layout',
        nodeType: nodeType ?? 'unknown',
        containerHeight: Math.round(container.getBoundingClientRect().height),
        editorHeight: Math.round(editor.getBoundingClientRect().height),
        flexFill: true,
        readOnly,
      })
    }

    const frameId = window.requestAnimationFrame(measureLayout)
    return () => window.cancelAnimationFrame(frameId)
  }, [nodeType, readOnly])

  return (
    <div className="h-full flex flex-col p-3 gap-2" data-testid="refine-tab-content">

      {/* Body.
          With an annotation surface the frames use TWO columns: prompt on the
          left, tools + the image being edited on the right, image BELOW the
          tools. Without one (ImageGenerator / StoryboardDesigner, which have
          no frame yet) the tab keeps its original single column. */}
      <div
        className={
          showAnnotationSurface
            ? 'flex min-h-0 flex-1 gap-2'
            : 'flex min-h-0 flex-1 flex-col gap-2'
        }
        data-testid="refine-body"
        data-layout={showAnnotationSurface ? 'two-column' : 'stacked'}
      >
        {/* Left — references, then the prompt. */}
        <div
          className="flex min-h-0 min-w-0 flex-1 flex-col gap-2"
        >
          {/* Figma Refine keeps references here and Style beside the model below. */}
          {showReferenceRow && (
            <ReferenceRow
              references={references}
              projectId={projectId}
              ownerNodeId={nodeId}
              onOpenSelector={() => setIsRefSelectorOpen(true)}
              onReferencesChange={onReferencesChange}
            />
          )}

          <div
            ref={feedbackAreaRef}
            className="flex min-h-0 flex-1 flex-col gap-1"
          >
            <label className="text-xxs uppercase tracking-wider text-text-muted">
              {t('configTabContent.prompt')}
            </label>
            <CanvasRichTextBox
              containerClassName="w-full flex-1 min-h-0 overflow-hidden bg-input-bg border border-input-border hover:border-input-border-hover rounded-lg px-3 py-2 transition-colors focus-within:border-primary/50 nodrag nowheel"
              ref={feedbackRef}
              containerTestId="refine-feedback-textarea"
              referenceNodeIds={references?.map(reference => reference.nodeId)}
              referenceOwnerNodeId={nodeId}
              referenceOwnerNodeType={nodeType ?? 'unknown'}
              placeholder={`${resolvedPlaceholder}\n${t('refineTabContent.submitShortcutHint')}`}
              initialContent={feedbackText}
              onContentChange={handleRichTextChange}
              onSubmit={() => {
                if (!isRunning && !isBaking && feedbackText.trim() && !isImageModelUnavailable) {
                  handleGenerate()
                }
              }}
              submitOnEnter={false}
              submitOnShiftEnter
              minHeight={40}
              maxHeight={120}
              maxLength={FEEDBACK_MAX_LENGTH}
              counterClassName="bg-input-bg"
              flexFill
              disabled={readOnly}
            />
          </div>

          {/* Prompt library — sits under the prompt and shares its width, so
              the two read as one column. Renders nothing for a node type with
              no curated set. */}
          {readOnly ? null : (
            <PromptLibrarySelect
              nodeType={nodeType}
              nodeId={nodeId}
              onSelect={handleLibraryInsert}
              disabled={isRunning}
            />
          )}
        </div>

        {/* Right — the annotation surface: tool rows, then the image under them. */}
        {showAnnotationSurface && annotationSourcePath && projectId && (
          <div
            className="flex min-w-0 flex-1 flex-col overflow-hidden"
            data-testid="refine-annotation-surface"
          >
            <AnnotationEditorOverlay
              // Keyed on the source so switching variant starts a clean sheet.
              // The editor holds its annotations in mount-scoped state, so
              // without this React reuses the instance and marks drawn on the
              // OLD variant survive onto the new one — then Generate bakes them
              // over an image they were never drawn against. Losing the marks
              // on a deliberate variant switch is the correct trade: they
              // describe a specific picture.
              key={annotationSourcePath}
              ref={annotationRef}
              presentation="inline"
              imageUrl={getAssetUrl(projectId, annotationSourcePath) ?? ''}
              altText={t('refineTabContent.annotationAlt')}
              traceId={`refine-annotation:${nodeId}`}
              nodeId={nodeId}
              isSaving={isBaking}
              // Inline has no commit buttons — Generate is the action — so
              // these are never reachable. They stay required on the shared
              // component because the lightbox does use them.
              onCancel={() => {}}
              onSave={() => {}}
            />
          </div>
        )}
      </div>

      {isImageModelUnavailable && (
        <div
          className="mx-3 mb-2 flex items-start gap-1.5 rounded-lg border border-warning/30 bg-warning/10 px-2 py-1.5 text-xs font-medium text-warning"
          role="alert"
          aria-live="polite"
          data-testid="refine-image-model-unavailable-alert"
        >
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{unavailableImageModelMessage}</span>
        </div>
      )}


      {/* Control Bar — hidden in read-only mode */}
      {readOnly ? null : <div className="shrink-0 flex items-center gap-2" data-testid="refine-control-bar">
        {/* Reuse Config's model picker; owners without a callback stay read-only. */}
        {model && (
          <>
            <button
              ref={modelPillRef}
              type="button"
              onClick={onModelChange ? () => {
                if (!isModelFlyoutOpen) {
                  trackControl('open_model')
                  logControl('refine-controls:model-picker-opened', { model_before: model })
                }
                setIsModelFlyoutOpen((open) => !open)
              } : undefined}
              disabled={!onModelChange}
              aria-haspopup="true"
              aria-expanded={isModelFlyoutOpen}
              className="flex shrink-0 items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-surface-1 text-muted-foreground border border-input-border transition-colors enabled:hover:bg-surface-3 enabled:hover:text-foreground enabled:hover:border-input-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default"
              data-testid="refine-model-pill"
              data-model={model}
              title={imageModelLabel}
            >
              <ImageIcon className="h-3 w-3" aria-hidden="true" />
              {imageModelLabel}
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            </button>
            <FlyoutPortal
              triggerRef={modelPillRef}
              isOpen={isModelFlyoutOpen}
              onClose={() => setIsModelFlyoutOpen(false)}
            >
              <ModelOnlyFlyout
                model={model}
                modelOptions={imageModelOptions}
                onModelChange={(next) => {
                  if (next !== model && onModelChange) {
                    onModelChange(next)
                    trackControl('select_model')
                    logControl('refine-controls:model-selected', { model_before: model, model_after: next })
                  }
                  setIsModelFlyoutOpen(false)
                }}
              />
            </FlyoutPortal>
          </>
        )}

        {onStyleChange && (
          <StyleSlot
            presentation="compact"
            currentStyleId={selectedStyleId}
            testId="refine-style-button"
            onOpen={() => {
              trackControl('open_style')
              logControl('refine-controls:style-library-opened', { style_before: selectedStyleId })
              setIsStyleModalOpen(true)
            }}
          />
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Retry Count — Collapsed Pill with Popover.
            Hidden when showRetrySelector is false (e.g. storyboard, whose
            backend ignores the count); retryCount then stays pinned to 1. */}
        <CreditQuantityPill sparks={sparksCount} sparksTestId="refine-sparks-count">
        {showRetrySelector && <div className="relative" data-testid="refine-retry-selector">
          <button
            ref={retryPillRef}
            onClick={() => setIsRetryPopoverOpen(!isRetryPopoverOpen)}
            className="px-1 text-xs font-medium text-text-muted hover:text-foreground transition-colors whitespace-nowrap tabular-nums"
            data-testid="refine-retry-pill"
          >
            {retryCount}x
          </button>

          {isRetryPopoverOpen && (
            <div
              ref={retryPopoverRef}
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-surface-1 border border-border rounded-lg shadow-2xl overflow-hidden z-50 min-w-[48px]"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              data-testid="refine-retry-popover"
            >
              {([4, 3, 2, 1]).map((n) => (
                <button
                  key={n}
                  onClick={() => {
                    setRetryCount(n)
                    setIsRetryPopoverOpen(false)
                  }}
                  className={`w-full px-3 py-1.5 text-xs font-medium text-center transition-colors ${
                    retryCount === n
                      ? 'text-primary-foreground'
                      : 'text-text-muted hover:text-foreground hover:bg-surface-2'
                  }`}
                  style={
                    retryCount === n
                      ? { backgroundColor: 'var(--primary)' }
                      : undefined
                  }
                  data-testid={`refine-retry-${n}x`}
                >
                  {n}x
                </button>
              ))}
            </div>
          )}
        </div>}
        </CreditQuantityPill>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={isRunning || isBaking || !feedbackText.trim() || isImageModelUnavailable}
          className="w-8 h-8 flex items-center justify-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ backgroundColor: 'var(--primary)' }}
          title={isImageModelUnavailable ? unavailableImageModelMessage : t('refineTabContent.generate')}
          data-testid="refine-generate-button"
        >
          <svg className="w-4 h-4 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      </div>}

      {/* Style library — the refine style button's target. */}
      {onStyleChange && (
        <StyleLibraryModal
          isOpen={isStyleModalOpen}
          onClose={() => setIsStyleModalOpen(false)}
          onApply={(styleId) => {
            onStyleChange(styleId)
            logControl('refine-controls:style-applied', { style_before: selectedStyleId, style_after: styleId })
            setIsStyleModalOpen(false)
          }}
          currentStyleId={selectedStyleId ?? ''}
        />
      )}

      {/* Reference picker — mounted only when the row is, so a node without
          references never pays for the modal's canvas scan. */}
      {showReferenceRow && (
        <ReferenceSelector
          isOpen={isRefSelectorOpen}
          onClose={() => setIsRefSelectorOpen(false)}
          onSave={handleRefSave}
          excludeNodeId={nodeId}
          canvasNodes={getNodes()}
          currentReferences={references}
          // Same capability-derived budget the Config tab uses — a reference
          // attached in Refine costs the provider exactly what one attached in
          // Config does, so the two pickers must not disagree on the cap.
          maxReferences={imageReferenceLimit}
          reservedStyleSlots={imageReservedStyleSlots}
          maxReferencesByMediaType={{ image: imageReferenceLimit }}
          refuseAtMediaLimit
          visibleTabs={['image']}
          targetBackendType={targetBackendType}
          onUploadCompleteReferencesChange={onReferencesChange}
        />
      )}
    </div>
  )
})

RefineTabContent.displayName = 'RefineTabContent'

export { RefineTabContent }
